// Copyright (c) 2020-2021. Sendanor <info@sendanor.fi>. All rights reserved.

import React from 'react';
import './PipelineRunView.scss';
import {ChangeCallback, VoidCallback} from "../../../../../nor/ts/interfaces/callbacks";
import FormAppClassName from "../../../../../constants/FormAppClassName";
import PipelineService from "../../../../../services/PipelineService";
import RepositoryEntry from "../../../../../nor/ts/simpleRepository/types/RepositoryEntry";
import LogService from "../../../../../nor/ts/LogService";
import Loader from "../../../../../nor/ui/components/loader/Loader";
import AppRoute from "../../../../../constants/AppRoute";
import RouteService from "../../../../../nor/ui/services/RouteService";
import PipelineRun from "../../../../common/pipeline/run/PipelineRun";
import PipelineRunDTO from "../../../../../nor/pipeline/dto/PipelineRunDTO";
import PipelineModel from "../../../../../nor/pipeline/types/PipelineModel";
import ControllerStateDTO from "../../../../../nor/pipeline/controllers/types/ControllerStateDTO";
import RepositoryMember from "../../../../../nor/ts/simpleRepository/types/RepositoryMember";
import MemberListModal from "../../../../../modals/MemberListModal";
import Button from "../../../../../nor/ui/components/button/Button";
import Icon from "../../../../../nor/ui/components/icon/Icon";
import { MembersIcon } from "../../../../../assets/icon";

const LOG = LogService.createLogger('PipelineRunView');

export enum PipelineRunViewComponentState {

    UNLOADED,
    LOADING,
    LOADED,
    LOAD_ERROR,

}

export interface PipelineRunViewState {

    readonly pipelineRunId    : string | undefined;
    readonly pipelineId       : string | undefined;
    readonly pipelineVersion  : number | undefined;
    readonly pipelineModel    : PipelineModel | undefined;
    readonly pipelineState    : ControllerStateDTO | undefined;
    readonly memberList       : RepositoryMember[];
    readonly componentState   : PipelineRunViewComponentState;
    readonly agentPoolId      : string | undefined;
    readonly agentAccountId   : string | undefined;

}

export interface PipelineRunViewProps {

    readonly id         : string;
    readonly runId      : string;
    readonly hasSession : boolean;

}

export class PipelineRunView extends React.Component<PipelineRunViewProps, PipelineRunViewState> {

    private readonly _changeModelCallback  : ChangeCallback<PipelineRunDTO>;
    private readonly _deleteCallback       : VoidCallback;
    private readonly _openMembersCallback          : VoidCallback;

    private _componentState : PipelineRunViewComponentState;

    public constructor (props: PipelineRunViewProps) {

        super(props);

        this._componentState = PipelineRunViewComponentState.UNLOADED;

        this.state = {
            pipelineId: undefined,
            pipelineRunId: undefined,
            pipelineModel: undefined,
            pipelineState: undefined,
            agentPoolId: undefined,
            agentAccountId: undefined,
            pipelineVersion: 0,
            componentState: this._componentState,
            memberList: []
        };

        this._changeModelCallback = this._onChangeModel.bind(this);
        this._deleteCallback      = this._onDelete.bind(this);
        this._openMembersCallback          = this._onOpenMembers.bind(this);

    }

    public componentDidMount (): void {

        this._loadBackendItem();

    }

    public componentWillUnmount (): void {

    }

    public componentDidUpdate (prevProps: Readonly<PipelineRunViewProps>, prevState: Readonly<PipelineRunViewState>, snapshot?: any): void {

        if (this.props.hasSession && !prevProps.hasSession && this.state.componentState !== PipelineRunViewComponentState.LOADING) {
            this._loadBackendItem();
        }

    }

    public render () {

        const pipelineRunId    = this.state?.pipelineRunId;
        const pipelineId       = this.state?.pipelineId;
        const pipelineModel    = this.state?.pipelineModel;
        const pipelineState    = this.state?.pipelineState;
        const pipelineVersion  = this.state.pipelineVersion;
        const componentState   = this.state.componentState;
        const isLoading        = componentState === PipelineRunViewComponentState.LOADING;

        // FIXME: Implement error state handling

        return (
            <div className={FormAppClassName.PIPELINE_RUN_VIEW}>

                <header className={FormAppClassName.PIPELINE_RUN_VIEW + '-header'}>

                    <h3 className={FormAppClassName.PIPELINE_RUN_VIEW + '-header-title'}>Pipeline run {pipelineModel?.name} v{pipelineVersion}</h3>

                    <div className={FormAppClassName.PIPELINE_RUN_VIEW + '-header-main-buttons'}>

                        <Button click={this._openMembersCallback}><Icon><MembersIcon /></Icon></Button>

                    </div>

                    <div className={FormAppClassName.PIPELINE_RUN_VIEW + '-header-side-buttons'}>

                        {/*{this.props.hasSession ? (*/}
                        {/*    <Button*/}
                        {/*        className={FormAppClassName.PIPELINE_RUN_VIEW + '-delete-button'}*/}
                        {/*        click={this._deleteCallback}><Icon><TrashIcon /></Icon> Cancel run</Button>*/}
                        {/*) : null}*/}

                    </div>

                </header>

                <div className={FormAppClassName.PIPELINE_RUN_VIEW + '-editor'}>

                    {this.props.hasSession ? (
                        <PipelineRun
                            className={FormAppClassName.PIPELINE_RUN_VIEW + '-editor-field'}
                            pipelineId={pipelineId}
                            pipelineRunId={pipelineRunId}
                            pipelineModel={pipelineModel}
                            pipelineState={pipelineState}
                        />
                    ) : (
                        <div>
                            <strong>You need to log in.</strong>
                        </div>
                    )}

                </div>

                {isLoading ? (
                    <Loader className={FormAppClassName.PIPELINE_RUN_VIEW + '-loader'} />
                ) : null}

            </div>
        );

    }


    private _setModel (run: PipelineRunDTO) {

        this.setState({
            pipelineRunId  : run?.id,
            pipelineId     : run?.pipelineId,
            pipelineModel  : run?.model,
            pipelineState  : run?.state,
            agentPoolId    : run?.agentPoolId,
            agentAccountId : run?.agentAccountId
        });

    }

    private _onChangeModel (model: PipelineRunDTO) {
        this._setModel(model);
    }

    private _loadBackendItem () {

        if ( this._componentState === PipelineRunViewComponentState.LOADING ) {
            LOG.warn(`_loadBackendItem: Already loading or saving`);
            return;
        }

        LOG.debug(`_loadBackendItem: Loading now.`);

        this._componentState = PipelineRunViewComponentState.LOADING;

        this.setState({componentState: this._componentState});

        PipelineService.getPipelineRun(this.props.id, this.props.runId).then((item : RepositoryEntry<PipelineRunDTO>) => {

            this._componentState = PipelineRunViewComponentState.LOADED;

            this.setState({
                pipelineRunId     : item.id,
                pipelineId        : item.data.pipelineId,
                pipelineVersion   : item.data.pipelineVersion,
                pipelineModel     : item.data.model,
                pipelineState     : item.data.state,
                componentState    : this._componentState,
                agentPoolId       : item.data.agentPoolId,
                agentAccountId    : item.data.agentAccountId,
                memberList        : item?.members ?? []
            });

        }).catch((err : any) => {

            this._componentState = PipelineRunViewComponentState.LOAD_ERROR;

            LOG.error(`_loadBackendItem: Loading failed: `, err);

            // FIXME: Display error

            this.setState({
                componentState: this._componentState
            });

        });

    }

    private _onDelete () {

        // FIXME: Implement a spinner
        // FIXME: Implement a confirmation or undo modal

        const id = this.props.id;
        PipelineService.deletePipeline(id).then(() => {

            LOG.info(`Removed ${id}`);

            RouteService.setRoute(AppRoute.PIPELINE_LIST_VIEW);

        }).catch(err => {

            // FIXME: Display UI error
            LOG.error(`Could not delete ${id}: `, err);

        });

    }

    private _onOpenMembers () {
        MemberListModal.openModal(this.state.memberList);
    }

}

export default PipelineRunView;
